import { useContext, useMemo } from 'react';

import CurrentUserContext from 'contexts/CurrentUserContext';
import ImpersonatorContext from 'contexts/ImpersonatorContext';
import { V3AppsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import appTypeCheckers from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider/appTypeCheckers';
import appNavItemTypeCheckers from 'types/appNavItemTypeCheckers';

/**
 * Whether the current user may override a given bonus category (a reward name) for a program.
 * App admins — and FleetOps staff when not impersonating — always can; otherwise only the
 * owner of a sign-off board that covers the category. Mirrors the per-criteria gate that lives
 * inline in Expectation's SetCriteriaOverride.
 */
const useCanOverrideCategory = ({
  bonusProgramId,
  category,
}: {
  bonusProgramId: string;
  category: string;
}): { isBoardOwner: boolean; isAppAdmin: boolean; canOverride: boolean } => {
  const currentUser = useContext(CurrentUserContext);
  const { impersonatorId } = useContext(ImpersonatorContext);
  const { availableProductApps } = useContext(V3AppsContext);

  const daxApp = useMemo((): V3.BonusApp | undefined => {
    return availableProductApps
      .filter(appTypeCheckers.isBonusApp)
      .find((app) => app.bonusProgramId === bonusProgramId);
  }, [availableProductApps, bonusProgramId]);

  const currentUserId = useMemo(() => {
    return impersonatorId ?? currentUser.id;
  }, [currentUser.id, impersonatorId]);

  const isBoardOwner = useMemo(() => {
    if (!daxApp) {
      return false;
    }
    const signOffBoards: V3.Navigation.SignOffBoard[] =
      daxApp.navigationSettings.sections.reduce(
        (acc: V3.Navigation.SignOffBoard[], section) => [
          ...acc,
          ...section.items.filter(appNavItemTypeCheckers.isSignOffBoard),
        ],
        [],
      );
    const ownedBoards = signOffBoards.filter((b) =>
      b.signOff.ownerIds.includes(currentUserId),
    );
    return ownedBoards.some((b) => b.categories.includes(category));
  }, [daxApp, category, currentUserId]);

  const isAppAdmin = useMemo(() => {
    if (!daxApp) {
      return false;
    }
    if (!impersonatorId && currentUser.isFleetOpsStaff) {
      return true;
    }
    return daxApp.adminUserIds.includes(currentUserId);
  }, [daxApp, currentUser.isFleetOpsStaff, currentUserId, impersonatorId]);

  return { isBoardOwner, isAppAdmin, canOverride: isBoardOwner || isAppAdmin };
};

export default useCanOverrideCategory;
